import { z } from 'zod';

export const PINNED_ITEM_TYPES = ['task', 'project', 'channel', 'team', 'message'] as const;
export type PinnedItemType = (typeof PINNED_ITEM_TYPES)[number];

export const CreatePinnedItemSchema = z.object({
  itemType: z.enum(PINNED_ITEM_TYPES),
  itemId: z.string().uuid(),
});
export type CreatePinnedItemInput = z.infer<typeof CreatePinnedItemSchema>;

/**
 * Team-visible facts of a pinned TASK, so the Favorites page can show status,
 * priority and deadline without a request per pin. Resolved under the
 * viewer's RLS context like the label — never anything private (no plan, no
 * notes). `statusCategory` comes from the project's effective workflow, the
 * same way list items get it, so "open" / "overdue" agree with every surface.
 */
export const PinnedTaskFactsSchema = z.object({
  projectId: z.string().uuid(),
  statusName: z.string().nullable(),
  statusColor: z.string().nullable(),
  statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  priority: z.number().int().min(1).max(4),
  /** Absolute UTC moment as ISO string; null when no deadline. */
  dueDate: z.string().nullable(),
  /** IANA zone of the project — the deadline's day is read there (like list items). */
  projectTimezone: z.string().optional(),
  archived: z.boolean(),
});
export type PinnedTaskFacts = z.infer<typeof PinnedTaskFactsSchema>;

export const PinnedItemProjectionSchema = z.object({
  itemType: z.enum(PINNED_ITEM_TYPES),
  itemId: z.string().uuid(),
  pinnedAt: z.string().datetime(),
  displayOrder: z.number().int(),
  /** Resolved display label — task title / project name / team name.  Null when
   *  the underlying item no longer exists or isn't visible to the user. */
  label: z.string().nullable().default(null),
  /** Secondary line — task key, project key, or team slug. */
  sublabel: z.string().nullable().default(null),
  /** Accent colour for the item's icon (projects / teams). */
  color: z.string().nullable().default(null),
  /** Team slug for navigation (project + team pins). */
  teamSlug: z.string().nullable().default(null),
  /** Project slug for navigation (project pins). */
  projectSlug: z.string().nullable().default(null),
  /** True when the underlying resource is gone / not visible — a stale pin. */
  missing: z.boolean().default(false),
  /** Task pins only: status / priority / deadline. Null for other types and stale pins. */
  task: PinnedTaskFactsSchema.nullable().default(null),
});
export type PinnedItemProjection = z.infer<typeof PinnedItemProjectionSchema>;

export const ReorderPinnedItemsSchema = z.object({
  orders: z
    .array(
      z.object({
        itemType: z.enum(PINNED_ITEM_TYPES),
        itemId: z.string().uuid(),
        displayOrder: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(200),
});
export type ReorderPinnedItemsInput = z.infer<typeof ReorderPinnedItemsSchema>;
